import { useState, useRef, useEffect } from 'react';
import { Bell, Moon, Sun, LogOut, UserCircle } from 'lucide-react';
import { SidebarTrigger } from '@/components/SidebarNew/ui/sidebar';
import styles from './DashboardHeader.module.scss';
import { useTranslation } from 'react-i18next';

interface DashboardHeaderProps {
  user: {
    email: string;
    fullname?: string;
    avatar_url?: string;
    language?: 'en' | 'vi';
  };
  onLogout?: () => void;
  onProfileClick?: () => void;
}

export function DashboardHeader({
  user,
  onLogout,
  onProfileClick,
}: DashboardHeaderProps) {
  const { t, i18n } = useTranslation();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isOpenLanguageMenu, setIsOpenLanguageMenu] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const lng = i18n.language || 'en';

  // Close user menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Check initial theme by looking at document class
  useEffect(() => {
    setIsDarkMode(document.documentElement.classList.contains('dark'));
  }, []);

  // Change theme
  const toggleTheme = () => {
    const newMode = !isDarkMode;
    setIsDarkMode(newMode);
    document.documentElement.classList.toggle('dark', newMode);
    localStorage.setItem('theme', newMode ? 'dark' : 'light');
  };

  const toggleCollapseLang = () => {
    setIsOpenLanguageMenu((prev) => !prev);
  };

  // Change language
  const changeLanguage = async (newLng: string) => {
    await i18n.changeLanguage(newLng);
    localStorage.setItem('i18nextLng', newLng);
    setIsOpenLanguageMenu(false);
  }

  // Get user initials
  const getInitials = () => {
    if (user.fullname) {
      return user.fullname
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    return user.email.charAt(0).toUpperCase();
  };

  return (
    <header className={styles.header}>
      {/* Left: Sidebar Toggle */}
      <div className={styles.headerLeft}>
        <SidebarTrigger />
      </div>

      {/* Right: Utilities */}
      <div className={styles.headerRight}>
        {/* Theme */}
        <button
          className={styles.iconButton}
          onClick={toggleTheme}
          title={isDarkMode ? t('header.lightMode') : t('header.darkMode')}
        >
          {isDarkMode ? (
            <Sun className={styles.icon} />
          ) : (
            <Moon className={styles.icon} />
          )}
        </button>

        {/* Language */}
        <div className="relative">
          <button
            className={styles.iconButton}
            onClick={toggleCollapseLang}
            title={t('header.language')}
          >
            { lng === 'en' ? (
              <img src="/images/flag/en.png" alt="English" className="!w-full !h-5" />
            ) : (
              <img src="/images/flag/vi.png" alt="Vietnamese" className="!w-full !h-5" />
            )}
          </button>
          {isOpenLanguageMenu && (
            <div className="absolute right-0 mt-2 w-40 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-50">
              <button
                className="flex items-center w-full px-4 py-2 text-black dark:!text-white hover:bg-gray-100 dark:hover:bg-gray-700"
                onClick={() => changeLanguage('en')}
              >
                <img src="/images/flag/en.png" alt="English" className="!w-8 !h-5 mr-2" />
                {t('header.english')}
              </button>
              <button
                className="flex items-center w-full px-4 py-2 text-black dark:!text-white hover:bg-gray-100 dark:hover:bg-gray-700"
                onClick={() => changeLanguage('vi')}
              >
                <img src="/images/flag/vi.png" alt="Vietnamese" className="!w-8 !h-5 mr-2" />
                {t('header.vietnamese')}
              </button>
            </div>
          )}
        </div>

        {/* Notifications */}
        <button className={styles.iconButton} title={t('header.notifications')}>
          <Bell className={styles.icon} />
          <span className={styles.notificationBadge}>0</span>
        </button>

        {/* User Menu */}
        <div className={styles.userMenuWrapper} ref={userMenuRef}>
          <button
            className={styles.userButton}
            onClick={() => setShowUserMenu(!showUserMenu)}
          >
            {user.avatar_url ? (
              <img
                src={user.avatar_url}
                alt={user.fullname || user.email}
                className={styles.userAvatar}
              />
            ) : (
              <div className={styles.userAvatarPlaceholder}>
                {getInitials()}
              </div>
            )}
          </button>

          {/* User Dropdown Menu */}
          {showUserMenu && (
            <div className={styles.userDropdown}>
              {/* User Info */}
              <div className={styles.userInfo}>
                <p className={styles.userName}>
                  {user.fullname || user.email.split('@')[0]}
                </p>
                <p className={styles.userEmail}>{user.email}</p>
              </div>

              <div className={styles.dropdownDivider} />

              {/* Menu Items */}
              <button
                className={styles.dropdownItem}
                onClick={() => {
                  onProfileClick?.();
                  setShowUserMenu(false);
                }}
              >
                <UserCircle className={styles.dropdownIcon} />
                <span>{t('header.profile')}</span>
              </button>

              <div className={styles.dropdownDivider} />

              <button
                className={`${styles.dropdownItem} ${styles.dropdownItemDanger}`}
                onClick={() => {
                  onLogout?.();
                  setShowUserMenu(false);
                }}
              >
                <LogOut className={styles.dropdownIcon} />
                <span>{t('header.logout')}</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
